import type {
  ExpenseBody,
  GenesisBody,
  GhostAddBody,
  GhostClaimBody,
  KeyRotateBody,
  MemberAddBody,
  MemberLeaveBody,
  MemberRemoveBody,
  Op,
  PaymentAckBody,
  PaymentBody,
  ProfileBody,
  Split,
  SupersedeBody,
} from "./op.js";
import { LIMITS, OP_VERSION, utf8Len, verifyOpIntegrity, WRAPPED_KEY_B64_LEN } from "./op.js";
import { isHex64 } from "./crypto.js";
import type { OpStore } from "./store.js";

export type ValidationResult = { ok: true } | { ok: false; reason: string };

const bad = (reason: string): ValidationResult => ({ ok: false, reason });

const isPubKey = isHex64;
const isHash = isHex64; // same shape: 32 bytes hex
const isAmount = (n: unknown): n is number =>
  typeof n === "number" && Number.isSafeInteger(n) && n > 0;
const isStr = (s: unknown, max: number): s is string =>
  typeof s === "string" && utf8Len(s) <= max;

// Membership as of a causal frontier: reduce only the ancestor closure of the
// given parent hashes. An op is authorized by what its author could SEE, so a
// concurrent leave elsewhere in the DAG does not retroactively invalidate it.
export interface Membership {
  members: Set<string>; // real keyholders: may author ops
  ghosts: Set<string>; // active unclaimed ghosts: may owe/pay, never author
  claims: Map<string, string>; // ghost id -> member pubkey it resolved to
}

export function membershipAt(store: OpStore, parents: string[]): Membership {
  const closure = store.ancestorsOf(parents);
  const members = new Set<string>();
  const ghosts = new Set<string>();
  const claims = new Map<string, string>();
  // topoOrder() is over the whole store; filter to the closure, order preserved.
  for (const op of store.topoOrder()) {
    if (!closure.has(op.hash)) continue;
    switch (op.kind) {
      case "genesis":
        members.add(op.author);
        break;
      case "member_add":
        members.add((op.body as MemberAddBody).member);
        break;
      case "member_leave":
        members.delete((op.body as MemberLeaveBody).member);
        break;
      // Deletion, so concurrent removals commute: two members who remove each
      // other both land, in any order, and both are out.
      case "member_remove":
        members.delete((op.body as MemberRemoveBody).member);
        break;
      case "ghost_add":
        ghosts.add((op.body as GhostAddBody).ghost);
        break;
      case "ghost_claim": {
        const body = op.body as GhostClaimBody;
        if (ghosts.has(body.ghost) && !claims.has(body.ghost)) {
          claims.set(body.ghost, body.member);
          ghosts.delete(body.ghost);
        }
        break;
      }
      default:
        break;
    }
  }
  return { members, ghosts, claims };
}

export function memberSetAt(store: OpStore, parents: string[]): Set<string> {
  return membershipAt(store, parents).members;
}

/**
 * Highest key epoch in the ancestor closure of `parents`; 0 means the group is
 * still on its founding key. Epoch numbering follows the same exact-successor
 * rule as lamport, so a hostile op cannot jump the counter and strand everyone.
 */
export function epochAt(store: OpStore, parents: string[]): number {
  const closure = store.ancestorsOf(parents);
  let epoch = 0;
  for (const op of store.topoOrder()) {
    if (!closure.has(op.hash) || op.kind !== "key_rotate") continue;
    const body = op.body as KeyRotateBody;
    if (body.epoch > epoch) epoch = body.epoch;
  }
  return epoch;
}

// `canOwe` is members plus unclaimed ghosts: everyone who can hold a balance.
function validateSplit(split: Split, amount: number, canOwe: Set<string>): ValidationResult {
  if (split.type !== "equal" && split.type !== "exact") return bad("unknown split type");
  const p = split.participants;
  if (!Array.isArray(p) || p.length === 0) return bad("split needs participants");
  if (p.length > LIMITS.participants) return bad("too many participants");
  if (new Set(p).size !== p.length) return bad("duplicate participants");
  for (const member of p) {
    if (!isPubKey(member)) return bad("participant is not a pubkey");
    if (!canOwe.has(member)) return bad("participant is not an active member");
  }
  if (split.type === "exact") {
    if (!Array.isArray(split.amounts) || split.amounts.length !== p.length) {
      return bad("exact split amounts must align with participants");
    }
    let sum = 0;
    for (const a of split.amounts) {
      if (!isAmount(a)) return bad("exact split amounts must be positive integers");
      sum += a;
    }
    if (sum !== amount) return bad("exact split amounts must sum to the expense amount");
  }
  return { ok: true };
}

function validateExpenseShape(body: ExpenseBody, canOwe: Set<string>): ValidationResult {
  if (!isStr(body.desc, LIMITS.desc) || body.desc.length === 0) return bad("bad desc");
  if (!isAmount(body.amount)) return bad("amount must be a positive integer (paise)");
  if (!isPubKey(body.paidBy)) return bad("bad paidBy");
  if (!canOwe.has(body.paidBy)) return bad("paidBy is not an active member");
  return validateSplit(body.split, body.amount, canOwe);
}

function validatePaymentShape(body: PaymentBody, canOwe: Set<string>): ValidationResult {
  if (!isPubKey(body.from) || !isPubKey(body.to)) return bad("bad payment endpoints");
  if (body.from === body.to) return bad("payment to self");
  if (!canOwe.has(body.from) || !canOwe.has(body.to)) return bad("payment endpoint not a member");
  if (!isAmount(body.amount)) return bad("amount must be a positive integer (paise)");
  if (!isStr(body.note, LIMITS.note)) return bad("bad note");
  return { ok: true };
}

// Full validation of an op against a store that already contains its parents.
// Used for BOTH remote ingest and locally created ops - local code paths get no
// shortcut, so an app bug cannot mint an op that other replicas would reject.
export function validateOp(store: OpStore, op: Op): ValidationResult {
  if (op.v !== OP_VERSION) return bad(`unsupported op version ${op.v}`);
  const integrity = verifyOpIntegrity(op);
  if (!integrity.ok) return integrity;
  if (!Array.isArray(op.parents) || op.parents.length > LIMITS.parents) return bad("bad parents");
  if (new Set(op.parents).size !== op.parents.length) return bad("duplicate parents");
  if (!Number.isSafeInteger(op.ts) || op.ts < 0) return bad("bad ts");
  if (!Number.isSafeInteger(op.lamport) || op.lamport < 0) return bad("bad lamport");

  if (op.kind === "genesis") {
    if (store.size > 0) return bad("group already has a genesis");
    if (op.group !== "") return bad("genesis group field must be empty");
    if (op.parents.length !== 0) return bad("genesis cannot have parents");
    if (op.lamport !== 0) return bad("genesis lamport must be 0");
    const body = op.body as GenesisBody;
    if (!isStr(body.name, LIMITS.name) || body.name.length === 0) return bad("bad group name");
    if (typeof body.currency !== "string" || !/^[A-Z]{3}$/.test(body.currency)) {
      return bad("currency must be a 3-letter ISO code");
    }
    return { ok: true };
  }

  if (store.size === 0) return bad("first op must be the genesis");
  if (op.group !== store.groupId) return bad("op belongs to a different group");
  if (op.parents.length === 0) return bad("non-genesis op needs parents");
  let maxParentLamport = -1;
  for (const parent of op.parents) {
    const parentOp = store.get(parent);
    if (!parentOp) return bad("missing parent");
    if (parentOp.lamport > maxParentLamport) maxParentLamport = parentOp.lamport;
  }
  if (op.lamport <= maxParentLamport) return bad("lamport must exceed all parents");
  // Must be EXACTLY one past the max parent - the value every honest op already
  // has (appendOp sets max(parents)+1). This rejects a hostile op that jumps the
  // clock toward MAX_SAFE_INTEGER (after which maxParent+1 would overflow and no
  // op could ever be appended). A fixed ceiling would not do: an op landing on
  // the ceiling still bricks its children (child = ceiling+1); exact-successor
  // has no such boundary.
  if (op.lamport > maxParentLamport + 1) return bad("lamport too large");

  const membership = membershipAt(store, op.parents);
  const { members, ghosts, claims } = membership;
  if (!members.has(op.author)) return bad("author is not an active member");
  const canOwe = new Set([...members, ...ghosts]);

  switch (op.kind) {
    case "member_add": {
      const body = op.body as MemberAddBody;
      if (!isPubKey(body.member)) return bad("bad member key");
      if (!isStr(body.name, LIMITS.name)) return bad("bad member name");
      if (members.has(body.member)) return bad("member already active");
      if (ghosts.has(body.member)) return bad("id belongs to a ghost; claim it instead");
      return { ok: true };
    }
    case "member_leave": {
      const body = op.body as MemberLeaveBody;
      if (body.member !== op.author) return bad("leaving is self-only; use member_remove");
      return { ok: true };
    }
    case "member_remove": {
      const body = op.body as MemberRemoveBody;
      if (!isPubKey(body.member)) return bad("bad member key");
      if (body.member === op.author) return bad("removing yourself is member_leave");
      if (!members.has(body.member)) return bad("member is not active");
      if (!isStr(body.reason, LIMITS.reason)) return bad("bad reason");
      return { ok: true };
    }
    case "key_rotate": {
      const body = op.body as KeyRotateBody;
      if (!Number.isSafeInteger(body.epoch)) return bad("bad epoch");
      // Exact successor, same reasoning as lamport: a hostile jump toward
      // MAX_SAFE_INTEGER would leave no valid successor and freeze rotation.
      if (body.epoch !== epochAt(store, op.parents) + 1) return bad("epoch must be exactly one past its parents");
      if (!isHex64(body.eph)) return bad("bad ephemeral key");
      if (!Array.isArray(body.keys)) return bad("bad wrapped keys");
      if (body.keys.length > LIMITS.wrappedKeys) return bad("too many wrapped keys");
      const seen = new Set<string>();
      let previous = "";
      for (const wrapped of body.keys) {
        if (typeof wrapped !== "object" || wrapped === null) return bad("bad wrapped key");
        if (!isPubKey(wrapped.m)) return bad("bad wrapped key member");
        if (typeof wrapped.k !== "string" || wrapped.k.length !== WRAPPED_KEY_B64_LEN) {
          return bad("bad wrapped key material");
        }
        // Sorted with no duplicates, so the op serializes identically wherever
        // it is rebuilt and a member cannot be quietly listed twice.
        if (wrapped.m <= previous) return bad("wrapped keys must be sorted by member");
        previous = wrapped.m;
        seen.add(wrapped.m);
      }
      // Every remaining member gets the new key, and nobody else does. This is
      // what makes a rotation an eviction: whoever left or was removed before
      // this op is simply absent from the list.
      if (seen.size !== members.size) return bad("wrapped keys must cover exactly the active members");
      for (const member of members) {
        if (!seen.has(member)) return bad("wrapped keys must cover exactly the active members");
      }
      return { ok: true };
    }
    case "ghost_add": {
      const body = op.body as GhostAddBody;
      if (!isPubKey(body.ghost)) return bad("bad ghost id");
      if (!isStr(body.name, LIMITS.name) || body.name.length === 0) return bad("ghost needs a name");
      if (members.has(body.ghost) || ghosts.has(body.ghost) || claims.has(body.ghost)) {
        return bad("ghost id already used");
      }
      return { ok: true };
    }
    case "ghost_claim": {
      const body = op.body as GhostClaimBody;
      if (body.member !== op.author) return bad("only self-claim is allowed");
      if (!ghosts.has(body.ghost)) return bad("ghost unknown or already claimed");
      return { ok: true };
    }
    case "expense":
      return validateExpenseShape(op.body as ExpenseBody, canOwe);
    case "payment":
      return validatePaymentShape(op.body as PaymentBody, canOwe);
    case "payment_ack": {
      const body = op.body as PaymentAckBody;
      if (!isHash(body.target)) return bad("bad ack target");
      const target = store.get(body.target);
      if (!target || target.kind !== "payment") return bad("ack target is not a payment");
      if ((target.body as PaymentBody).to !== op.author) {
        return bad("only the receiver can confirm a payment");
      }
      return { ok: true };
    }
    case "supersede": {
      const body = op.body as SupersedeBody;
      if (!isHash(body.target)) return bad("bad supersede target");
      const target = store.get(body.target);
      if (!target) return bad("supersede target unknown");
      if (target.kind !== "expense" && target.kind !== "payment") {
        return bad("only expenses and payments can be superseded");
      }
      if (target.author !== op.author) return bad("only the author can edit their entry");
      if (body.replacement === null) return { ok: true };
      // Replacement is validated against membership at THIS op - participants
      // who have since left must be dropped from edits (documented limitation).
      if (target.kind === "expense") {
        const r = body.replacement as ExpenseBody;
        if (typeof r !== "object" || r === null || !("split" in r)) return bad("replacement kind mismatch");
        return validateExpenseShape(r, canOwe);
      }
      const r = body.replacement as PaymentBody;
      if (typeof r !== "object" || r === null || !("from" in r)) return bad("replacement kind mismatch");
      return validatePaymentShape(r, canOwe);
    }
    case "profile": {
      const body = op.body as ProfileBody;
      if (!isStr(body.name, LIMITS.name)) return bad("bad profile name");
      if (!isStr(body.vpa, LIMITS.vpa)) return bad("bad vpa");
      return { ok: true };
    }
    default:
      return bad(`unknown kind ${(op as Op).kind}`);
  }
}

// The single write path: validate, then add.
export function validateAndAdd(store: OpStore, op: Op): ValidationResult {
  if (store.has(op.hash)) return { ok: true }; // idempotent
  const result = validateOp(store, op);
  if (result.ok) store.add(op);
  return result;
}
